<template>
  <section class="container-fluid">
    <b-modal size="md" hide-footer no-close-on-backdrop @hidden="onReset" ref="projectModal" static centered :modal-class="isModalLoading ? 'loading' : ''">
      <template v-slot:modal-header="{ close }">
        <h5 class="modal-title">
          {{ isEdit ? 'Edit project' : 'Create project' }}
        </h5>
        <button type="button" aria-label="Close" class="close" @click.prevent="close">
          <span class="material-icons">
            close
          </span>
        </button>
      </template>

      <preloader v-if="isDataPageLoading" />
      <b-form class="form-generator row" @submit.prevent="submit" @reset="onReset">
        <b-form-group class="col-6">
          <label>Project manager</label>
          <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="form.project_manager ? form.project_manager.name : null">
            <multiselect v-model="form.project_manager" :options="getProjectManagersList" value-field="id"
              placeholder="Project Manager" label="name" :showLabels="false" :class="{ 'multiselect__single-line': true }" />
          </div>
        </b-form-group>
        <b-form-group class="col-6">
          <label>Account manager</label>
          <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="form.account_manager ? form.account_manager.name : null">
            <multiselect v-model="form.account_manager" :options="getAccountManagersList" value-field="id"
              placeholder="Account Manager" label="name" :showLabels="false" :class="{ 'multiselect__single-line': true }" />
          </div>
        </b-form-group>

        <b-form-group class="col-6">
          <label>Business developer</label>
          <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="form.business_developer ? form.business_developer.name : null">
            <multiselect v-model="form.business_developer" :options="getBusinessDevelopersList" value-field="id"
              placeholder="Business Developer" label="name" :showLabels="false" :class="{ 'multiselect__single-line': true }" />
          </div>
        </b-form-group>

        <b-form-group class="col-6">
          <label>Pre-sales manager</label>
          <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="form.pre_sales_manager ? form.pre_sales_manager.name : null">
            <multiselect v-model="form.pre_sales_manager" :options="getBusinessDevelopersList" value-field="id"
              placeholder="Pre-sales manager" label="name" :showLabels="false" :class="{ 'multiselect__single-line': true }" />
          </div>
        </b-form-group>

        <b-form-group class="col-6">
          <label>Sales channel</label>
          <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="form.sales_channel ? form.sales_channel.name : null">
            <multiselect v-model="form.sales_channel" :options="salesChannels" value-field="id"
              placeholder="Sales channel" label="name" :showLabels="false" :class="{ 'multiselect__single-line': true }" required />
          </div>
        </b-form-group>
        <b-form-group class="col-6 btn-icon button-checkbox_sm mb-0 switch-group">
          <b-form-checkbox v-model="withIntegration" name="check-button" size="sm" class="switch" switch>
            Project with integration with PM tool
          </b-form-checkbox>
        </b-form-group>
        <div v-if="!withIntegration" class="mt-2 row col-12 pl-1 pr-0">
          <b-form-group class="col-6">
            <label>Project key</label>
            <b-form-input class="mb-3" v-model="form.project_key" maxlength="5" placeholder="Project key" />
          </b-form-group>
          <b-form-group class="col-6 pr-0">
            <label>Project name</label>
            <b-form-input class="mb-3" v-model="form.project_name" maxlength="50" placeholder="Project name" />
          </b-form-group>
          <b-form-group class="col-12 mb-0 tasks-container">
            <div class="d-flex justify-content-between w-100 pr-2 align-items-center">
              <h6>Tasks</h6>
              <div class="d-flex align-items-center">
                <b-button @click.prevent="addTask" class="add-button">
                  Add task
                </b-button>
                <span v-if="form.tasks.length !== 0" @click="showTasks" class="material-icons expand-icon">
                  expand_more
                </span>
              </div>
            </div>
            <b-table v-if="form.tasks && form.tasks.length && isShowTasks" :fields="taskFields" :items="form.tasks"
              class="mb-0 tasks-table">
              <template #cell(key)="{ item, index }">
                <b-form-input v-model="item.key" maxlength="10" placeholder="Task key" />
              </template>
              <template #cell(name)="{ item, index }">
                <b-form-input v-model="item.name" maxlength="50" placeholder="Task name" />
              </template>
              <template #cell(actions)="{ item, index }">
                <b-button variant="danger" class="delete-button" @click.prevent="removeTask(index)">
                  <font-awesome-icon icon="trash" />
                </b-button>
              </template>
            </b-table>
          </b-form-group>
        </div>
        <div v-if="withIntegration" class="col-12 px-0">
          <div class="d-flex px-0 flex-wrap">
            <b-form-group class="col-6">
              <label>PM tool</label>
              <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="form.external_project">
                <multiselect v-model="form.external_project" :options="projectIntegrationOptions" required
                  :showLabels="false" :class="{ 'multiselect__single-line': true }" />
              </div>
            </b-form-group>
            <b-form-group class="col-6 btn-icon w-100 mb-0 switch-group button-checkbox_sm">
              <b-form-checkbox id="is_custom_project" v-model="form.is_custom_project" name="is_custom_project"
                value="1" size="sm" class="switch" switch>
                Custom project (without jira board)
              </b-form-checkbox>
            </b-form-group>
            <b-form-group class="col-12" v-if="!form.is_custom_project">
              <label>Jira project</label>
              <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="form.project ? form.project.name : null">
                <multiselect v-if="!form.is_custom_project" v-model="form.project" :options="getJiraProjectsList" required
                  placeholder="Jira Project" label="name" :showLabels="false" :class="{ 'multiselect__single-line': true }" />
              </div>
            </b-form-group>
            <div v-else class="col-12 d-flex px-0">
              <b-form-group class="col-4">
                <label>Project key</label>
                <b-form-input class="mb-3" v-model="form.custom_project_key" maxlength="5" placeholder="Project key" />
              </b-form-group>
              <b-form-group class="col-4">
                <label>Project name</label>
                <b-form-input class="mb-3" v-model="form.custom_project_name" maxlength="50"
                  placeholder="Project name" />
              </b-form-group>
              <b-form-group class="col-4">
                <label>Jira task KEY</label>
                <b-form-input v-model="form.custom_project_task_for_logging_time" maxlength="100"
                  placeholder="Jira task KEY" />
              </b-form-group>
            </div>
          </div>
        </div>
        <div class="d-flex details-container px-1 mb-2 justify-content-between w-100 align-items-center">
          <h6>Details</h6>
          <span @click="showDetails" class="material-icons details-expand expand-icon">
            expand_less
          </span>
        </div>
        <div class="col-12 row" v-if="isShowDetails">
          <b-form-group class="col-12">
            <div class="row">
              <div class="col-6">
                <label>Project types</label>
                <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="form.project_type">
                  <multiselect v-model="form.project_type" :options="projectTypesOptions" required
                    placeholder="Select type" :showLabels="false" :class="{ 'multiselect__single-line': true }" />
                </div>
              </div>
              <div class="col-6 pr-0">
                <label>Project status</label>
                <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="form.status">
                  <multiselect v-model="form.status" :options="projectStatuses" required placeholder="Select status"
                    :showLabels="false" :class="{ 'multiselect__single-line': true }" />
                </div>
              </div>
            </div>
          </b-form-group>
          <b-form-group class="col-6">
            <label>Agreement url</label>
            <b-form-input v-model="form.agreement_url" maxlength="2000" placeholder="Agreement url" />
          </b-form-group>
          <b-form-group class="col-6 pr-0">
            <label>Agreement number</label>
            <b-form-input v-model="form.agreement_number" maxlength="255" placeholder="Agreement number" />
          </b-form-group>
          <b-form-group class="col-12 pr-0">
            <div class="row">
              <div class="col-6">
                <label>Project priority</label>
                <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="form.priority ? form.priority.name : null">
                  <multiselect v-model="form.priority" :options="priorities" required placeholder="Priority"
                    class="project-select" label="name" :showLabels="false" :class="{ 'multiselect__single-line': true }" />
                </div>
              </div>
              <!--            <div class="col-4">
              <label>Budget risks %*</label>
              <b-form-input
                v-model="form.budget_risks"
                maxlength="10"
                placeholder="Budget risks %"/>
            </div>-->
              <div class="col-6 pl-0">
                <label class="pl-1">Project color</label>
                <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="form.color ? form.color.name : null">
                  <multiselect v-model="form.color" :options="config.colors" placeholder="Color"
                    class="project-select multiselect-color color-select" label="name" :showLabels="false" :class="{ 'multiselect__single-line': true }" required>
                  <template slot="singleLabel" slot-scope="{ option }">
                    <div class="multiselect-color__project-box" :style="{ 'background-color': option.hex }"></div>
                    <span> Color {{ getColorIndex(option.hex) + 1 }} </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <div class="option__desc">
                      <div class="multiselect-color__box" :style="{ 'background-color': props.option.hex }"></div>
                      {{ props.option.name }}
                    </div>
                  </template>
                  </multiselect>
                </div>
              </div>
            </div>
          </b-form-group>
        </div>
        <b-form-group class="col-12 form-generator__controls">
          <div class="form-generator-buttons">
            <b-button type="reset" variant="outline" class="button mr-2 btn-format" v-if="!isEdit">
              Reset
            </b-button>
            <b-button type="submit" variant="primary" class="button btn-format" :disabled="isModalLoading">
               <b-spinner v-if="isModalLoading" small class="mr-2"></b-spinner>
              {{isModalLoading ? 'LOADING...' : 'Save'}}
            </b-button>
          </div>
        </b-form-group>
      </b-form>
    </b-modal>
    <div class="row">
      <div class="col-12">
        <div class="page-table-header full-height-page-wrapper">
        <PageHeader showBreadcrumbs page-title="Project List">
          <template #buttons>
            <b-button class="button title-container__controls-item" :variant="isShowFilter ? 'info' : 'secondary'"
              @click="toggleShowFilter">
              <span class="material-icons icon-image-preview"> tune </span>
              Filters
            </b-button>
            <b-button v-if="$can('create-projects')" variant="success" class="button" @click="create"
              :disabled="isDataPageLoading">
              Create
            </b-button>
          </template>
        </PageHeader>
        <Filters ref="filters" v-show="isShowFilter" textButtonSubmit="Filter" resetToInitial :data="filters"
          :isLoading="isDataPageLoading" @submit="filterProject" />

        <div v-if="isDataPageLoading || !hasInitialFilteringCompleted" class="content-loading-wrapper">
          <Preloader class="centered-preloader" />
        </div>
        <div v-else-if="projects.length === 0 && getProjects.length === 0" class="full-height-content">
          <EmptyStateNoResults
            customIcon="folder_off"
            customIconClasses="material-symbols-outlined empty-state__icon"
            title="No projects yet"
            description="Get started by creating your first project. Click the button below to begin."
            buttonText="Create Project"
            @button-click="handleCreateClick"
          />
        </div>
        <div v-else-if="projects.length === 0 && getProjects.length > 0" class="full-height-content">
          <EmptyStateNoResults @reset-filters="handleResetFilters" />
        </div>
        <TableForPage ref="table" style="margin-bottom: 0" v-else :tableData="projects" :fields="fields"
          :slots="tableSlots" :isEdit="$can('update_all-projects')" :isRemove="$can('create-projects')" @edit="edit"
          @remove="remove">
          <template v-slot:name="data">
            <template v-if="data.item.project_type.name !== 'internal'">
              <a :href="`https://giraffesoftware.atlassian.net/browse/${getProjectDashboardKey(data.item)}`"
                target="_blank">
                {{ data.item.name }}
              </a>
            </template>
            <template v-else>
              {{ data.item.name }}
            </template>
          </template>
          <template v-slot:color="slotProps">
            <div class="project-color">
              <span :style="{ backgroundColor: slotProps.item.color || '#4080BF' }">
              </span>
            </div>
          </template>
          <template v-slot:project_status.name="slotProps">
            <span class="project-status" :class="slotProps.item.project_status.name">
              {{ slotProps.item.project_status.name }}
            </span>
          </template>
          <template v-slot:priority="slotProps">
            {{ findPriorityById(slotProps.item.priority) }}
          </template>
        </TableForPage>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import Preloader from '@/components/Preloader.vue'
import TableForPage from '@tables/TableForPage'
import Filters from '@components/Filter/Filter'
import { deepFindObjectValueByPath } from '../functions/deepFindObjectValueByPath'
import PageHeader from '@components/PageHeader.vue'
import EmptyStateNoResults from '@components/EmptyStateNoResults'

export default {
  name: 'Projects',
  components: {
    Preloader,
    TableForPage,
    Filters,
    PageHeader,
    EmptyStateNoResults
  },
  data () {
    return {
      isShowFilter: true,
      withIntegration: false,
      isShowDetails: true,
      isShowTasks: true,
      filters: {
        'projects': {
          type: 'multiselect',
          name: 'projects',
          label: 'Select projects',
          valueField: 'name',
          selectLabel: 'name',
          options: [],
          value: [],
          class: 'col-md-2',
          pathToObjectValue: 'name'
        },
        'project_manager': {
          type: 'multiselect',
          name: 'project_manager',
          label: 'Select Project manager',
          valueField: 'name',
          selectLabel: 'name',
          options: [],
          value: [],
          class: 'col-md-3',
          pathToObjectValue: 'project_manager.name'
        },
        'account_manager': {
          type: 'multiselect',
          name: 'account_manager',
          label: 'Select Account manager',
          valueField: 'name',
          selectLabel: 'name',
          options: [],
          value: [],
          class: 'col-md-2',
          pathToObjectValue: 'account_manager.name'
        },
        'business_developer': {
          type: 'multiselect',
          name: 'business_developer',
          label: 'Select Business developer',
          valueField: 'name',
          selectLabel: 'name',
          options: [],
          value: '',
          class: 'col-md-3',
          pathToObjectValue: 'business_developer.name'
        },
        'project_status': {
          type: 'multiselect',
          name: 'project_status',
          label: 'Select Status',
          selectLabel: null,
          options: [],
          value: '',
          defaultValue: '',
          class: 'col-md-2',
          pathToObjectValue: 'project_status.name'
        }
      },
      config: {
        colors: [
          { name: 'Red', hex: '#DA4F57' },
          { name: 'Pink', hex: '#D81B60' },
          { name: 'Purple', hex: '#AB47BC' },
          { name: 'Blue', hex: '#477DD0' },
          { name: 'Cyan', hex: '#0097A7' },
          { name: 'Green', hex: '#43A047' },
          { name: 'Orange', hex: '#EF6C00' },
          { name: 'Brown', hex: '#8D6E63' },
          { name: 'Amber', hex: '#FF8F00' },
          { name: 'Light Green', hex: '#9CCC65' }
        ]
      },
      transProps: {
        // Transition name
        name: 'flip-list'
      },
      isDataPageLoading: false,
      isModalLoading: false,
      hasInitialFilteringCompleted: false,

      fields: [
        {
          label: 'Color',
          key: 'color',
          sortable: false,
          class: 'column-status'
        },
        {
          label: 'Priority',
          key: 'priority',
          sortable: false,
          class: 'column-priority'
        },
        {
          label: 'Name',
          key: 'name',
          sortable: true,
          class: 'column-long-project-name'
        },
        {
          label: 'Log task',
          key: 'task_for_logging_time',
          sortable: true,
          class: 'column-log-task',
          formatter: (value) => {
            return value ? value.join(',') : ''
          }
        },
        {
          label: 'PM',
          key: 'project_manager.name',
          sortable: true,
          class: 'column-long-name'
        },
        {
          label: 'AM',
          key: 'account_manager.name',
          sortable: true,
          class: 'column-manager-name'
        },
        {
          label: 'BD',
          key: 'business_developer.name',
          sortable: true,
          class: 'column-manager-name'
        },
        {
          label: 'Status',
          key: 'project_status.name',
          sortable: true,
          class: 'column-project-status'
        },
        {
          key: 'actions',
          label: 'Actions',
          class: 'column-controls'
        }
      ],
      taskFields: [
        { key: 'key', label: 'Key', class: 'select-column col-6' },
        { key: 'name', label: 'Name', class: 'select-column' },
        { key: 'actions', label: '', class: 'actions-column-project' }
      ],
      form: {
        id: null,
        project_manager: '',
        account_manager: '',
        business_developer: '',
        pre_sales_manager: '',
        is_custom_project: false,
        sales_channel: null,
        custom_project_key: '',
        custom_project_name: '',
        custom_project_task_for_logging_time: '',
        status: 'development',
        project: '',
        project_key: '',
        project_name: '',
        external_project: 'Jira',
        priority: {
          id: 3,
          name: 'Medium'
        },
        budget_risks: '0',
        project_type: 'external',
        agreement_url: '',
        agreement_number: '',
        color: { name: 'Red', hex: '#DA4F57' },
        tasks: []
      },
      jiraProjects: [],
      projects: [],
      perPage: 15,
      projectTypesOptions: [
        'internal',
        'external'
      ],
      projectIntegrationOptions: ['Jira'],
      tableSlots: {
        name: {
          name: 'name'
        },
        color: {
          name: 'color'
        },
        priority: {
          name: 'priority'
        },
        'project_status.name': {
          name: 'project_status.name'
        }
      }
    }
  },
  watch: {
    'withIntegration': function (val) {
      if (!val) {
        this.form.tasks = []
      }
    }
  },
  computed: {
    ...mapGetters({
      priorities: 'getPriorities',
      projectStatuses: 'getProjectStatuses',
      salesChannels: 'finances/getSalesChannels'
    }),
    getProjects () {
      return [...this.$store.getters['projects/getProjects']]
        .sort((project1, project2) => project1.created_at < project2.created_at ? 1 : -1)
    },
    getJiraProjectsList () {
      if (!this.$store.getters['projects/getJiraProjects'].length) return []
      return [...this.$store.getters['projects/getJiraProjects']].sort((project1, project2) => project1.name < project2.name ? -1 : 1)
    },
    getBusinessDevelopersList () {
      if (!this.$store.getters['businessDevelopers/getBusinessDevelopers'].length) return []
      return [...this.$store.getters['businessDevelopers/getBusinessDevelopers']].sort((user1, user2) => user1.name < user2.name ? -1 : 1)
    },
    getProjectManagersList () {
      if (!this.$store.getters['users/getProjectManagers'].length) return []
      return [...this.$store.getters['users/getProjectManagers']].sort((user1, user2) => user1.name < user2.name ? -1 : 1)
    },
    getAccountManagersList () {
      if (!this.$store.getters['users/getAccountManagers'].length) return []
      return [...this.$store.getters['users/getAccountManagers']].sort((user1, user2) => user1.name < user2.name ? -1 : 1)
    },
    isEdit () {
      return this.form.id
    },
    checkFormForEmptyFields () {
      /* eslint-disable camelcase */
      /* eslint-disable no-mixed-operators */
      const {
        project_manager,
        account_manager,
        project,
        project_key,
        project_name,
        priority,
        budget_risks,
        custom_project_key,
        custom_project_name,
        custom_project_task_for_logging_time,
        is_custom_project,
        status,
        project_type,
        business_developer,
        agreement_url,
        agreement_number
      } = this.form

      if (!business_developer || !project_manager && !account_manager || !priority || !status || !budget_risks || !project_type) {
        return true
      }

      if (this.withIntegration) {
        if (is_custom_project) {
          return !custom_project_name || !custom_project_key || !custom_project_task_for_logging_time
        } else {
          return !project
        }
      } else {
        if (!project_key || !project_name) {
          return true
        }
      }

      if ((agreement_url && !agreement_number) || (agreement_number && !agreement_url)) {
        return true
      }

      return false
    },
    formDefault () {
      const defaultSalesChannel = this.salesChannels.find(item => item.is_default)
      return {
        id: null,
        project_manager: '',
        account_manager: '',
        business_developer: '',
        pre_sales_manager: '',
        is_custom_project: false,
        custom_project_key: '',
        custom_project_name: '',
        custom_project_task_for_logging_time: '',
        sales_channel: defaultSalesChannel,
        status: '',
        project_type: '',
        project: '',
        project_key: '',
        project_name: '',
        external_project: 'Jira',
        priority: '',
        budget_risks: '0',
        color: {
          hex: '#4080BF'
        },
        agreement_url: '',
        agreement_number: '',
        tasks: []
      }
    }
  },
  created () {
    this.isDataPageLoading = true
    this.fetchAllData()
      .finally(() => {
        this.isDataPageLoading = false
      })
  },
  methods: {
    isItemEqualFilter (filters, filterKey, item) {
      const keyForFilterItem = filters[filterKey]['valueField']
      return filters[filterKey]['value'].some(filterItem => (filterItem[keyForFilterItem] || filterItem) === item)
    },
    getColorIndex (hex) {
      return this.config.colors.findIndex(color => color.hex === hex)
    },
    filterProject (selectedFilters) {
      this.projects = this.getProjects.filter(item => {
        let isAllEqual = true

        for (const key in selectedFilters) {
          const isItemEqual = this.isItemEqualFilter(
            selectedFilters,
            key,
            deepFindObjectValueByPath(item, selectedFilters[key]['pathToObjectValue'])
          )

          if (!isItemEqual) {
            isAllEqual = false
            break
          }
        }
        return isAllEqual
      })

      this.resetPagination()
    },
    resetPagination () {
      this.$refs['table'] && this.$refs['table'].resetPagination()
    },
    fetchAllData () {
      return Promise.all([
        this.fetchProjects(),
        this.$api.users.getProjectManagers(),
        this.$api.users.getAccountManagers(),
        this.$api.businessDevelopers.getBusinessDevelopers(),
        this.$api.projects.getJiraProjects(),
        this.$api.finances.getSalesChannels()
      ])
        .then(() => {
          this.setAllOptionsFilter()
        })
    },
    setAllOptionsFilter () {
      this.filters.project_manager.options = this.getProjectManagersList
      this.filters.account_manager.options = this.getAccountManagersList
      this.filters.business_developer.options = this.getBusinessDevelopersList
      this.filters.project_status.options = this.projectStatuses
      this.filters.project_status.value = ['predevelopment', 'development']
      this.filters.project_status.defaultValue = ['predevelopment', 'development']
      this.filters.projects.options = this.getProjects
      this.filterProject({ project_status: this.filters.project_status })
      this.hasInitialFilteringCompleted = true
    },
    toggleShowFilter () {
      this.isShowFilter = !this.isShowFilter
    },
    showModal () {
      this.$refs['projectModal'].show()
    },
    hideModal () {
      this.$refs['projectModal'].hide()
      this.onReset()
    },
    create () {
      this.form.sales_channel = this.salesChannels.find(item => item.is_default)
      this.showModal()
    },
    async edit (project) {
      this.form = { ...project,
        priority: this.findPriorityById(project.priority, true),
        budget_risks: project.budget_risks ? project.budget_risks : '0',
        project: '',
        project_key: project.key,
        project_name: project.name,
        custom_project_key: '',
        custom_project_name: '',
        custom_project_task_for_logging_time: '',
        agreement_url: project.agreement_url ? project.agreement_url : '',
        agreement_number: project.agreement_number ? project.agreement_number : '',
        color: { hex: project.color },
        status: project.project_status.name,
        project_type: project.project_type.name,
        sales_channel: project.sales_channel,
        pre_sales_manager: project.pre_sales_manager,
        tasks: []
      }
      this.withIntegration = !!(project.external || project.is_custom_project)
      if (this.withIntegration) {
        this.form.external_project = project.external_type === `App\\JiraProject` ? 'Jira' : ''

        if (project.is_custom_project) {
          this.form.custom_project_key = project.key
          this.form.custom_project_name = project.name
          this.form.custom_project_task_for_logging_time = project.task_for_logging_time.join(',')
        } else if (!project.is_custom_project) {
          this.form.project = this.findProjectBId(project.id, true)
        }
      } else {
        const tasksResponse = await this.$api.tasks.getTasks({ project_ids: [project.id] })
        this.form.tasks = tasksResponse.data
      }
      this.showModal()
    },
    prepareFormDataForRequest () {
      /* eslint-disable camelcase */
      const {
        color,
        priority,
        budget_risks,
        project,
        project_manager,
        account_manager,
        business_developer,
        is_custom_project,
        custom_project_key,
        custom_project_name,
        custom_project_task_for_logging_time,
        status,
        project_type,
        sales_channel,
        pre_sales_manager,
        agreement_url,
        agreement_number,
        tasks
      } = this.form

      let project_key, project_name
      if (this.withIntegration) {
        project_key = is_custom_project ? custom_project_key : project.key
        project_name = is_custom_project ? custom_project_name : project.name
      } else {
        project_key = this.form.project_key
        project_name = this.form.project_name
      }

      return {
        /* eslint-disable camelcase */
        project_manager_id: project_manager ? project_manager.id : undefined,
        account_manager_id: account_manager ? account_manager.id : undefined,
        business_developer_id: business_developer ? business_developer.id : undefined,
        pre_sales_manager_id: pre_sales_manager ? pre_sales_manager.id : null,
        sales_channel_id: sales_channel ? sales_channel.id : null,
        is_custom_project: this.withIntegration ? Number(is_custom_project) : 0,
        key: project_key,
        name: project_name,
        task_for_logging_time: is_custom_project && this.withIntegration ? custom_project_task_for_logging_time.split(',') : project.task_for_logging_time,
        color: color.hex,
        priority: priority.id,
        budget_risks: budget_risks,
        status: status ? status.toLowerCase() : '',
        project_type,
        agreement_url,
        agreement_number,
        tasks,
        withIntegration: this.withIntegration
      }
    },
    submit () {
      if (this.isEdit) {
        this.editProject()
      } else {
        this.createProject()
      }
    },
    createProject () {
      this.isModalLoading = true

      this.$api.projects.create({ ...this.prepareFormDataForRequest() }
      )
        .then(result => {
          this.$snotify.success(
            'Project successfully created',
            'Success',
            this.$store.state.snotify
          )
          this.reloadProjects()
          this.hideModal()
        })
        .catch(err => {
          if (err.response) {
            this.errorNotifications(err)
          }
        }).finally(() => {
          this.isModalLoading = false
        })
    },
    editProject () {
      this.isModalLoading = true
      this.$api.projects.update(this.form.id, { ...this.prepareFormDataForRequest() })
        .then(result => {
          this.$snotify.success(
            'Project successfully saved',
            'Success',
            this.$store.state.snotify
          )
          this.reloadProjects()
          this.hideModal()
        })
        .catch(err => {
          if (err.response) {
            this.errorNotifications(err)
          }
        }).finally(() => {
          this.isModalLoading = false
        })
    },
    onReset () {
      this.form = { ...this.formDefault, color: { ...this.formDefault.color } }
      /* Trick to reset/clear native browser form validation state */
      this.$nextTick(() => {
        this.show = true
      })
      this.withIntegration = false
    },
    showTasks () {
      if (this.form.tasks && this.form.tasks.length) {
        this.isShowTasks = !this.isShowTasks
      } else {
        this.isShowTasks = true
      }
    },
    showDetails () {
      this.isShowDetails = !this.isShowDetails
    },
    fetchProjects () {
      return this.$api.projects.getProjects()
    },
    reloadProjects () {
      this.fetchProjects().then(() => {
        this.filterProject(this.$refs.filters.selectedFilters)
      })
    },
    async remove (item) {
      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete the project?',
        {
          submitText: 'Delete'
        }
      )
      if (!confirm) return

      this.$root.$confirm.setLoading(true)
      this.$api.projects.remove(
        item.id
      )
        .then(data => {
          this.reloadProjects()
          this.$snotify.success(
            'Project successfully removed',
            'Success',
            this.$store.state.snotify
          )
          this.$root.$confirm.setLoading(false)
          this.$root.$confirm.dialog = false
        })
        .catch(err => {
          var errors = Object.values(err.response.data.errors)
          for (var i = 0; i < errors.length; i++) {
            this.$snotify.error(
              errors[i][0],
              err.response.data.message ?? 'Error',
              Object.assign({}, this.$store.state.snotify, { timeout: 7000 })
            )
          }
          this.$root.$confirm.setLoading(false)
        })
    },
    findPriorityById (id = 2, returnObject) {
      const priority = this.priorities.find(priority => priority.id === id)
      if (returnObject) {
        return priority
      }
      return priority.name
    },
    findProjectBId (id, returnObject) {
      const project = this.projects.find(priority => priority.id === id)
      if (returnObject) {
        return project
      }
      return project.name
    },
    getProjectDashboardKey (item) {
      return item.is_custom_project ? 'OUT' : item.key
    },
    addTask () {
      this.form.tasks.push({
        key: '',
        name: ''
      })
    },
    removeTask (index) {
      this.form.tasks.splice(index, 1)
    },
    handleResetFilters () {
      if (this.$refs.filters) {
        this.$refs.filters.resetFilter()
      }
    },
    handleCreateClick () {
      this.create()
    }
  }
}
</script>

<style lang="scss" scoped>
.project-status {
  &.development {
    color: #3d5170;
  }
  &.done {
    color: #3fa52f;
  }
  &.predevelopment {
    color: #dc3545;
  }
}

.project-color {
  width: 24px;
  height: 24px;
  span {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 3px;
  }
}
.color-picker {
  width: 100% !important;
}
.loader {
  position: relative;
  background-color: transparent;
}

.form-generator {
  margin-right: -5px;
  margin-left: -5px;

  label {
    font-size: 12px;
    margin-bottom: 0;
    color: $inputLabelColor1;
  }
}
.switch-group {
  margin-top: 1.7rem;
}

.add-button {
  background-color: $notifySuccessColor;
  min-width: 206px;
  border: 0;
  font-size: 14px;
  font-weight: 700;
  font-family: $font-family-second;
  text-transform: uppercase;
  &:focus,
  &:hover,
  &:active {
    background-color: $notifySuccessColor;
    box-shadow: none;
  }
}

.expand-icon {
  font-size: 25px;
  margin-left: 10px;
  margin-bottom: 3px;
  margin-right: 2px;
  cursor: pointer;
}

.details-expand {
  margin-left: 0;
  margin-right: 15px;
}

.delete-button {
  width: 50px;
  height: $input-height;
  display: flex;
  justify-content: center;
  align-items: center;
}

.tasks-container {
  border-top: 1px solid $tableBorderColor;
  padding: 15px 0 15px 0;
}

.details-container {
  border-top: 1px solid $tableBorderColor;
  padding-top: 15px;
}

.btn-format {
  width: 180px;
  height: $input-height;
  display: flex;
  justify-content: center;
}

.multiselect-color__project-box {
  width: 20px;
  height: 20px;
}
</style>
